import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, useParams, Navigate } from 'react-router-dom'
import { MainLayout } from '@/layouts/MainLayout'
import { HomePage } from '@/pages/HomePage'
import { GamesPage } from '@/pages/GamesPage'
import { GameDetailsPage } from '@/pages/GameDetailsPage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import { TermsOfServicePage } from '@/pages/TermsOfServicePage'
import { CookieSettingsPage } from '@/pages/CookieSettingsPage'
import { StandaloneGamePage } from '@/pages/StandaloneGamePage'
import { ErrorPage } from '@/pages/ErrorPage'
import { BlogsPage } from '@/pages/BlogsPage'
import { BlogPostPage } from '@/pages/BlogPostPage'
import { GameLoader } from '@/components/ui/GameLoader'
import { getGameById } from '@/data/games'
import { applyGameManifest } from '@/hooks/usePWA'

// Lazy load game components from organized structure
const TicTacToe = lazy(() => import('@/games/classic/TicTacToe').then(m => ({ default: m.TicTacToe })))
const Snake = lazy(() => import('@/games/arcade/Snake').then(m => ({ default: m.Snake })))
const Breakout = lazy(() => import('@/games/arcade/Breakout').then(m => ({ default: m.Breakout })))
const Pong = lazy(() => import('@/games/arcade/Pong').then(m => ({ default: m.Pong })))
const MemoryMatch = lazy(() => import('@/games/puzzle/MemoryMatch').then(m => ({ default: m.MemoryMatch })))
const CarRacing = lazy(() => import('@/games/racing/CarRacing').then(m => ({ default: m.CarRacing })))
const CarDodger = lazy(() => import('@/games/racing/CarDodger').then(m => ({ default: m.CarDodger })))
const NeonDrift = lazy(() => import('@/games/racing/neon-drift').then(m => ({ default: m.NeonDrift })))
const SkyStack = lazy(() => import('@/games/casual/sky-stack').then(m => ({ default: m.SkyStack })))
const JumpBox = lazy(() => import('@/games/casual/jump-box').then(m => ({ default: m.JumpBox })))

// Game components mapping by component name
const gameComponentsMap = {
  'TicTacToe': TicTacToe,
  'Snake': Snake,
  'Breakout': Breakout,
  'Pong': Pong,
  'MemoryMatch': MemoryMatch,
  'CarRacing': CarRacing,
  'CarDodger': CarDodger,
  'NeonDrift': NeonDrift,
  'SkyStack': SkyStack,
  'JumpBox': JumpBox,
}

// Dynamic game loader by ID - applies per-game manifest for PWA
function DynamicGameLoader() {
  const { gameId } = useParams()
  const game = getGameById(gameId)

  // Check if running as installed PWA (standalone mode)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true

  // Apply game-specific manifest for PWA installation
  useEffect(() => {
    if (game) {
      const cleanup = applyGameManifest(game)
      document.title = `${game.title} | Polytoria`
      return cleanup
    }
  }, [game])

  // Prevent back navigation in PWA standalone mode
  useEffect(() => {
    if (!isStandalone) return

    // Replace current history state so there's nothing to go back to
    window.history.replaceState(null, '', window.location.href)

    // Push a dummy state so back button triggers popstate
    window.history.pushState(null, '', window.location.href)

    const handlePopState = (e) => {
      // When user presses back, push state again to stay on game
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isStandalone])

  if (!game || !game.playable || !game.component) {
    return <Navigate to="/" replace />
  }

  const GameComponent = gameComponentsMap[game.component]
  
  if (!GameComponent) {
    return <Navigate to="/" replace />
  }

  return (
    <Suspense fallback={<GameLoader />}>
      <GameComponent gameInfo={game} />
    </Suspense>
  )
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      path: '/',
      element: <MainLayout />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: 'games',
          element: <GamesPage />,
        },
        {
          path: 'game/:gameId',
          element: <GameDetailsPage />,
        },
        {
          path: 'privacy',
          element: <PrivacyPolicyPage />,
        },
        {
          path: 'terms',
          element: <TermsOfServicePage />,
        },
        {
          path: 'cookies',
          element: <CookieSettingsPage />,
        },
        {
          path: 'blogs',
          element: <BlogsPage />,
        },
        {
          path: 'blogs/:slug',
          element: <BlogPostPage />,
        },
      ],
    },
    // Playable games routes - dynamic by game ID
    {
      path: '/play/:gameId',
      element: <DynamicGameLoader />,
      errorElement: <ErrorPage />,
    },
    // Standalone game player with custom frame
    {
      path: '/standalone/:gameId',
      element: <StandaloneGamePage />,
      errorElement: <ErrorPage />,
    },
    // Catch-all for 404
    {
      path: '*',
      element: <ErrorPage />,
    },
  ])
}
